import {
  DerivedDefinitionType,
  IDataModelerDraftEdits,
  IDataModelerDraftFieldData,
  IDerivedColumnDraft,
} from '../../../../redux/slices/dataModeler';
import {
  getColumnDefinitionMetaDisplay,
  listToHashMapSkipNulls,
  shortTableKey,
} from '../utils';
import {
  FieldType,
  IColumnDefinitionMeta,
  ICustomColumnDefinitionInput,
  IDataModelEditColumnDefinitionInput,
  IFieldTypeParamsFieldsFragment,
  ISchemaDataModelFieldsFragment,
  ITableFieldFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../../utils/types';
import {
  customColumnDefinitionToInput,
  DATA_MODEL_FIELD_TYPE_CONFIG,
  typeParamsInputToTypeParamsFieldsFragment,
} from '../../../../redux/slices/dataModeler/utils';
import React from 'react';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react';
import { MenuItemHeaderDiv } from './DataModelerDerivedColumnCopyDefinitionUI';
import { UnreachableCaseError } from 'ts-essentials';
import ContextualMenuOption from '../../../../components/Dropdown/ContextualMenu/ContextualMenuOption';

interface IAvailableDefinitionToCopy {
  tableAndColumnKey: string;
  viewKey: string;
  tableKey: string;
  columnName: string;
  fieldType: FieldType;
  typeParams: IFieldTypeParamsFieldsFragment;
  definitionType: DerivedDefinitionType | null;
  definition: ICustomColumnDefinitionInput | null;
}

const getTableAndColumnKey = (tableKey: string, column: string) =>
  `${tableKey}-${column}`;

const getSelectableDefinitionToCopyFromMeta = (
  viewKey: string,
  tableField: ITableFieldFieldsFragment,
  derivedDefinition: IColumnDefinitionMeta,
  editInput: IDataModelEditColumnDefinitionInput | null,
): IAvailableDefinitionToCopy => {
  let definitionType: DerivedDefinitionType | null = null;
  switch (derivedDefinition.__typename) {
    case 'RawSqlDefinitionMeta':
      definitionType = 'RawSql';
      break;
    case 'MappedStringDefinitionMeta':
      definitionType = 'MappedString';
      break;
    default:
      throw new UnreachableCaseError(derivedDefinition);
  }
  const definition =
    editInput?.customDefinition ??
    customColumnDefinitionToInput(derivedDefinition);
  return {
    tableAndColumnKey: getTableAndColumnKey(
      tableField.tableKey,
      tableField.column,
    ),
    viewKey,
    tableKey: tableField.tableKey,
    columnName: tableField.column,
    fieldType: tableField.type,
    typeParams: tableField.typeParams,
    definitionType,
    definition,
  };
};

const getSelectableDefinitionToCopyFromDraft = (
  factViewKey: string,
  draftField: IDataModelerDraftFieldData,
): IAvailableDefinitionToCopy | null => {
  const typeInput =
    draftField.addInput?.typeInput ?? draftField.extendInput?.typeInput;
  if (!typeInput?.typeParams?.customDefinition) {
    return null;
  }
  const viewKey = draftField.addInput?.viewKey ?? factViewKey;
  const definition = typeInput?.typeParams?.customDefinition;
  let definitionType: DerivedDefinitionType | null = definition?.mappedString
    ? 'MappedString'
    : 'RawSql';
  return {
    tableAndColumnKey: getTableAndColumnKey(
      draftField.tableKey,
      draftField.column.column,
    ),
    viewKey,
    tableKey: draftField.tableKey,
    columnName: draftField.column.column,
    fieldType: typeInput.type,
    typeParams: typeParamsInputToTypeParamsFieldsFragment(
      draftField.column.column,
      typeInput.typeParams,
    ),
    definitionType,
    definition,
  };
};

const getConfirmedAvailableDefinitions = (
  schemaObj: ISchemaDataModelFieldsFragment,
  editColumnInputs: IDataModelEditColumnDefinitionInput[],
): IAvailableDefinitionToCopy[] => {
  const draftEditColumns = listToHashMapSkipNulls(editColumnInputs, (item) =>
    getTableAndColumnKey(item.tableKey, item.column),
  );
  return [
    { viewKey: schemaObj.fact.key, fields: schemaObj.fact.fields },
    ...schemaObj.dimensions.map(({ key, fields }) => {
      return { viewKey: key, fields };
    }),
  ].flatMap(({ viewKey, fields }) => {
    return fields.flatMap((schemaField) => {
      return schemaField.tableFields.flatMap((tableField) => {
        if (!tableField.typeParams.customDefinition) {
          return [];
        }
        return [
          getSelectableDefinitionToCopyFromMeta(
            viewKey,
            tableField,
            tableField.typeParams.customDefinition,
            draftEditColumns[
              getTableAndColumnKey(tableField.tableKey, tableField.column)
            ] ?? null,
          ),
        ];
      });
    });
  });
};

const getNewAvailableDefinitions = (
  draftFieldsData: IDataModelerDraftFieldData[],
  factViewKey: string,
): IAvailableDefinitionToCopy[] => {
  return draftFieldsData.flatMap((draftField) => {
    const ret = getSelectableDefinitionToCopyFromDraft(factViewKey, draftField);
    return !!ret ? [ret] : [];
  });
};

const loadedSchemaToCopyDefinitionMenuItems = (
  schemaObj: ISchemaDataModelFieldsFragment,
  draftEdits: IDataModelerDraftEdits,
  updateDraft: (props: Partial<IDerivedColumnDraft>) => void,
): IContextualMenuItem[] => {
  const forSchema = schemaObj.key;
  const confirmedDefinitions = getConfirmedAvailableDefinitions(
    schemaObj,
    draftEdits.editColumnInputs,
  );
  const newDefinitions = getNewAvailableDefinitions(
    draftEdits.draftFieldsData,
    schemaObj.fact.key,
  );
  const availableDefinitionsToCopy = [
    ...confirmedDefinitions,
    ...newDefinitions,
  ];
  const onSelectDefinition = (tableAndColumnKey: string) => {
    const toCopy = availableDefinitionsToCopy.find(
      (definition) => definition.tableAndColumnKey === tableAndColumnKey,
    );
    if (!toCopy) {
      return;
    }
    updateDraft({
      columnName: toCopy.columnName,
      fieldType: toCopy.fieldType,
      typeParams: toCopy.typeParams,
      originalTypeParams: null,
      definitionType: toCopy.definitionType,
      definition: toCopy.definition,
    });
  };
  if (availableDefinitionsToCopy.length === 0) {
    return [
      {
        key: `${forSchema}-loading`,
        text: 'No definitions found',
      },
    ];
  }
  const byTableKey: {
    [key: string]: IAvailableDefinitionToCopy[] | undefined;
  } = {};
  const tableKeys: string[] = [];
  availableDefinitionsToCopy.forEach((def) => {
    if (!byTableKey[def.tableKey]) {
      byTableKey[def.tableKey] = [];
      tableKeys.push(def.tableKey);
    }
    byTableKey[def.tableKey]?.push(def);
  });
  const tableItems = tableKeys.map((tableKey) => {
    const tableDefinitions = byTableKey[tableKey] ?? [];
    return {
      key: `${forSchema}-${tableKey}`,
      onRenderContent: () => (
        <ContextualMenuOption
          label={shortTableKey(forSchema, tableKey)}
          hasSubMenus={true}
        />
      ),
      subMenuProps: {
        items: tableDefinitions.map((def): IContextualMenuItem => {
          assertNotNil(def.typeParams.customDefinition);
          const { typeText, details } = getColumnDefinitionMetaDisplay(
            def.typeParams.customDefinition,
          );
          const contentPopupText = `${typeText}: ${details}`;
          return {
            key: `${forSchema}-${def.tableAndColumnKey}`,
            onRenderContent: () => (
              <ContextualMenuOption
                label={
                  <>
                    {def.columnName} (
                    {DATA_MODEL_FIELD_TYPE_CONFIG[def.fieldType].humanName})
                  </>
                }
                onClick={() => onSelectDefinition(def.tableAndColumnKey)}
                tooltipOption={{
                  content: contentPopupText,
                }}
              />
            ),
          };
        }),
      },
    };
  });
  return [
    {
      key: `schema-${schemaObj.key}-tables`,
      onRender: () => <MenuItemHeaderDiv>Table</MenuItemHeaderDiv>,
    },
    {
      key: `schema-${schemaObj.key}-separator`,
      itemType: ContextualMenuItemType.Divider,
    },
    ...tableItems,
  ];
};

export default loadedSchemaToCopyDefinitionMenuItems;
